import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { captureStoresCartViewed } from '@/mixpanel/MixpanelEvents';
import {
  isStoreCartPagePath,
  syncStoreCartOpenQueryParam,
  shouldSyncStoreCartOpenUrl,
  isStoreCartOpenQueryParam,
} from '@/modules/Cart/StoreCart/helpers/storeCartNavigation.utils';
import { STORE_CART_OPEN_QUERY_KEY } from '@/modules/Cart/StoreCart/storeCart.constants';

let wasStoreCartOpen = false;

export const resetStoreCartOpenTrackingStateForTests = () => {
  wasStoreCartOpen = false;
};

/** Keeps `storeCartOpen=true` in the URL while the store cart is open. */
export const useStoreCartOpenUrlSync = (isStoreCartOpen: boolean) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const isStoreCartOpenRef = useRef(isStoreCartOpen);
  isStoreCartOpenRef.current = isStoreCartOpen;

  useEffect(() => {
    if (isStoreCartOpen && !wasStoreCartOpen) {
      captureStoresCartViewed();
    }

    wasStoreCartOpen = isStoreCartOpen;
  }, [isStoreCartOpen]);

  useEffect(
    () => () => {
      wasStoreCartOpen = false;
    },
    []
  );

  useEffect(() => {
    if (!shouldSyncStoreCartOpenUrl(pathname)) {
      return;
    }

    const hasParam = isStoreCartOpenQueryParam(searchParams);

    if (isStoreCartOpen && !hasParam) {
      setSearchParams(syncStoreCartOpenQueryParam(searchParams, true), { replace: true });
      return;
    }

    if (!isStoreCartOpen && hasParam) {
      setSearchParams(syncStoreCartOpenQueryParam(searchParams, false), { replace: true });
    }
  }, [isStoreCartOpen, pathname, searchParams, setSearchParams]);

  useEffect(
    () => () => {
      if (!isStoreCartOpenRef.current) {
        return;
      }

      if (!shouldSyncStoreCartOpenUrl(globalThis.location.pathname)) {
        return;
      }

      // Overlay routes keep `storeCartOpen` via RightSection; only strip on the page route.
      if (!isStoreCartPagePath(globalThis.location.pathname)) {
        return;
      }

      const params = new URLSearchParams(globalThis.location.search);
      if (params.get(STORE_CART_OPEN_QUERY_KEY) !== 'true') {
        return;
      }

      params.delete(STORE_CART_OPEN_QUERY_KEY);
      const search = params.toString();
      const nextUrl = search
        ? `${globalThis.location.pathname}?${search}`
        : globalThis.location.pathname;
      globalThis.history.replaceState(null, '', nextUrl);
    },
    []
  );
};
